import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { get, ref, update } from 'firebase/database';
import { auth, db, isFirebaseConfigured, signInStatsViewerWithGoogle, signOutStatsViewer } from './lib/firebase';
import { buildUpdatePayload, normalizeUpdateConfig, validateUpdateConfig } from './lib/updateConfig';
import './force-update.css';

const labels = {
  minimumVersion: 'أقل إصدار مسموح',
  updateUrl: 'رابط التحديث في المتجر'
};

export default function ForceUpdatePage() {
  const [viewer, setViewer] = useState(null);
  const [status, setStatus] = useState('loading');
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setError('إعدادات Firebase غير متاحة.');
      setStatus('denied');
      return undefined;
    }
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const current = ++generation;
      setViewer(user && !user.isAnonymous ? user : null);
      setConfigs(null);
      setError('');
      setNotice('');
      if (!user || user.isAnonymous) { setStatus('login'); return; }
      setStatus('loading');
      try {
        const allowed = user.email?.toLowerCase() === 'shayee@gmail.com' ||
          (await get(ref(db, `admins/${user.uid}`))).val() === true;
        if (current !== generation) return;
        if (!allowed) { setStatus('denied'); return; }
        const snapshot = await get(ref(db, 'app_config'));
        if (current !== generation) return;
        const data = snapshot.val() || {};
        setConfigs({ android: normalizeUpdateConfig(data.android), ios: normalizeUpdateConfig(data.ios) });
        setStatus('ready');
      } catch (err) {
        if (current !== generation) return;
        setError(`تعذر تحميل الإعدادات: ${err.code || err.message}`);
        setStatus('denied');
      }
    });
    return () => { generation += 1; unsubscribe(); };
  }, []);

  async function login() {
    setBusy(true); setError('');
    try { await signInStatsViewerWithGoogle(); }
    catch (err) { setError(`تعذر تسجيل الدخول: ${err.code || err.message}`); }
    finally { setBusy(false); }
  }

  async function save(event) {
    event.preventDefault();
    if (status !== 'ready' || busy || auth.currentUser?.uid !== viewer?.uid) return;
    setError(''); setNotice('');
    for (const platform of ['android', 'ios']) {
      const config = { ...configs[platform], minimumVersion: configs[platform].minimumVersion.trim(), updateUrl: configs[platform].updateUrl.trim() };
      const problem = validateUpdateConfig(config, platform);
      if (problem) { setError(`${platform === 'ios' ? 'iOS' : 'Android'}: ${problem}`); return; }
    }
    setBusy(true);
    try {
      await update(ref(db, 'app_config'), buildUpdatePayload(configs));
      setNotice('تم حفظ الإعدادات. يقرأها تطبيق الجوال عند فحص التحديث التالي.');
    } catch (err) { setError(`تعذر الحفظ: ${err.code || err.message}`); }
    finally { setBusy(false); }
  }

  return (
    <main className="update-admin" dir="rtl">
      <header><div><a href="/stats">← الإحصائيات</a><h1>التحديث الإجباري</h1></div>
        {viewer && <button className="update-logout" type="button" disabled={busy} onClick={() => signOutStatsViewer().catch(() => setError('تعذر تسجيل الخروج.'))}>خروج</button>}
      </header>
      {status === 'loading' && <p role="status">جاري التحقق من الصلاحية وتحميل الإعدادات…</p>}
      {status === 'login' && <section><h2>تسجيل دخول المدير</h2><p>استخدم حساب Google المصرح له بإدارة الإحصائيات.</p><button type="button" disabled={busy} onClick={login}>{busy ? 'جاري تسجيل الدخول…' : 'المتابعة بحساب Google'}</button></section>}
      {status === 'denied' && <p>لا يمكن فتح الإدارة. هذه الصفحة متاحة لمدير الإحصائيات فقط.</p>}
      {error && <p className="update-error" role="alert">{error}</p>}
      {notice && <p className="update-success" role="status">{notice}</p>}
      {status === 'ready' && configs && <form onSubmit={save}>
        <p className="update-help">فعّل التحديث وحدد أقل إصدار مسموح بعد توفره في المتجر.</p>
        <div className="update-platforms">{['android', 'ios'].map((platform) => <fieldset key={platform} disabled={busy}>
          <legend>{platform === 'ios' ? 'iOS — App Store' : 'Android — Google Play'}</legend>
          <label className="update-toggle" htmlFor={`${platform}-enabled`}>
            <span>تفعيل التحديث الإجباري</span>
            <input id={`${platform}-enabled`} type="checkbox" role="switch" checked={configs[platform].enabled} onChange={(event) => {
              const enabled = event.target.checked;
              setConfigs((old) => ({ ...old, [platform]: { ...old[platform], enabled } }));
              setNotice('');
            }} />
            <span className="update-toggle-state">{configs[platform].enabled ? 'مفعّل' : 'متوقف'}</span>
          </label>
          {Object.entries(labels).map(([key, label]) => <label key={key} htmlFor={`${platform}-${key}`}>{label}
            <input id={`${platform}-${key}`} dir="ltr" type="text" disabled={!configs[platform].enabled} value={configs[platform][key]} maxLength={500} placeholder={key === 'minimumVersion' ? '1.0.0' : 'https://…'} onChange={(event) => {
              const value = event.target.value;
              setConfigs((old) => ({ ...old, [platform]: { ...old[platform], [key]: value } }));
              setNotice('');
            }} />
          </label>)}
        </fieldset>)}</div>
        <footer><span>الإصدارات الأقدم فقط سيُطلب منها التحديث.</span><button type="submit" disabled={busy}>{busy ? 'جاري الحفظ…' : 'حفظ التغييرات'}</button></footer>
      </form>}
    </main>
  );
}
